import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { map, take } from 'rxjs';

import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessagesModule } from 'primeng/messages';
import { InputTextarea } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';

import { BlogNewsService } from 'src/app/services/blog-news.service';
import { BlogPost } from 'src/app/models/blog-post';
import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';

const BLOGS_NEWS_EDIT_FLAG = 'blogs-news-edit';

/** Editor-only: filter chips for publication state (`accepted`), archive state, and untagged posts. Multi-select, OR'd together. */
export type BlogPublicationFilter = 'published' | 'draft' | 'archived' | 'untagged';

/**
 * Blogs and news page component.
 */
@Component({
	selector: 'app-blogs-news',
	standalone: true,
	imports: [CommonModule, CardModule, ProgressSpinnerModule, MessagesModule, ButtonModule, InputTextarea, InputTextModule, SelectButtonModule, DialogModule, FormsModule, TooltipModule],
	providers: [BlogNewsService],
	templateUrl: './blogs-news.component.html',
	styleUrl: './blogs-news.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogsNewsComponent {
	private blogNewsService = inject(BlogNewsService);

	private launchDarklyService = inject(LaunchDarklyService);

	private domSanitizer = inject(DomSanitizer);

	private route = inject(ActivatedRoute);

	/**
	 * Title of the page.
	 */
	public title = 'Blogs & News';

	/**
	 * Traton Jira issue browse base URL (ticket key is appended).
	 * @see https://jiratoolchain.collaborationlayer-traton.com/browse/
	 */
	public readonly jiraBrowseBaseUrl = 'https://jiratoolchain.collaborationlayer-traton.com/browse/';

	/**
	 * Blog posts with initial loading flag (GraphQL + presigned JSON fetch).
	 */
	public blogPostsView$ = this.blogNewsService.blogPostsView$;

	/** Placeholder indices for skeleton cards while posts load. */
	public readonly skeletonPlaceholders = Array.from({ length: 9 }, (_, i) => i);

	/**
	 * Whether current user can edit blog/news posts based on feature flag.
	 */
	public canEdit$ = this.launchDarklyService.launchDarklyFlags$.pipe(map(flags => !!flags[BLOGS_NEWS_EDIT_FLAG]));

	/**
	 * State management.
	 */
	public isLoading = signal(false);

	public error = signal('');

	/**
	 * Search and filter state.
	 */
	public searchQuery = signal('');

	public activeLabel = signal<string | null>(null);

	/** Local datetime strings from `datetime-local` (`YYYY-MM-DDTHH:mm`); empty = no bound */
	public filterDateFrom = signal('');

	public filterDateTo = signal('');

	/** Editors only: active publication/archive chips (multi-select, OR'd together). Empty = all posts. */
	public publicationFilters = signal<ReadonlySet<BlogPublicationFilter>>(new Set());

	constructor() {
		const labelFromUrl = this.route.snapshot.queryParamMap.get('label');
		if (labelFromUrl?.trim()) {
			this.activeLabel.set(labelFromUrl.trim());
		}
	}

	/**
	 * Editing state for inline edit.
	 */
	public editingIndex: number | null = null;

	public editedTextValue: string = '';

	public editedTitle: string = '';

	public editedAccepted: boolean = false;

	public editedIssueLabels: string = '';

	/** Jira issue key (e.g. PROJ-123); editors only */
	public editedIssueKey: string = '';

	public editedArchived: boolean = false;

	public acceptedOptions = [
		{ label: 'Yes', value: true },
		{ label: 'No', value: false },
	];

	public archivedOptions = [
		{ label: 'Yes', value: true },
		{ label: 'No', value: false },
	];

	/**
	 * Modal editor state.
	 */
	public isEditorDialogVisible: boolean = false;

	public dialogContextPost: BlogPost | null = null;

	/**
	 * Read More dialog state.
	 */
	public isReadMoreDialogVisible: boolean = false;

	public readMorePost: BlogPost | null = null;

	/**
	 * Pagination: number of posts to display initially and increment size.
	 */
	public displayLimit = signal(12);

	public increaseLimit(step: number = 12): void {
		this.displayLimit.update(current => current + step);
	}

	/**
	 * Typical Jira issue key: project key (letters + digits) + hyphen + number (e.g. PROJ-123).
	 * Rejects placeholders and values that would only open the browse root without a ticket.
	 */
	private static readonly jiraIssueKeyPattern = /^[A-Za-z][A-Za-z0-9_]{1,30}-\d+$/;

	/**
	 * True when `issueKey` is a non-empty, valid Jira issue key (safe to show link).
	 * @param issueKey
	 */
	public hasJiraIssueKey(issueKey: string | undefined | null): boolean {
		const key = issueKey != null ? String(issueKey).trim() : '';
		if (!key) {
			return false;
		}
		const lower = key.toLowerCase();
		if (lower === 'null' || lower === 'undefined' || lower === 'n/a' || lower === 'none' || lower === '-' || lower === '—') {
			return false;
		}
		return BlogsNewsComponent.jiraIssueKeyPattern.test(key);
	}

	/**
	 * Full Jira URL for an issue key (opens in new tab from template).
	 * Only valid when {@link hasJiraIssueKey} is true; otherwise returns empty string.
	 * @param issueKey
	 */
	public jiraIssueBrowseUrl(issueKey: string | undefined | null): string {
		if (!this.hasJiraIssueKey(issueKey)) {
			return '';
		}
		const key = String(issueKey).trim();
		return `${this.jiraBrowseBaseUrl}${encodeURIComponent(key)}`;
	}

	/**
	 * Issue labels for chips, excluding the same text as `issueKey` so the ticket is not shown twice (chip + meta link).
	 * @param post
	 */
	public labelsForDisplay(post: BlogPost): string[] {
		if (!post.issueLabels?.length) {
			return [];
		}
		const rawIk = post.issueKey != null ? String(post.issueKey).trim() : '';
		const ikLower = rawIk.toLowerCase();
		return post.issueLabels
			.map(l => String(l).trim())
			.filter(l => l.length > 0)
			.filter(l => !rawIk || l.toLowerCase() !== ikLower);
	}

	/**
	 * Extracts all unique, visible labels from a list of posts (same set as {@link labelsForDisplay} per post,
	 * so a label hidden because it duplicates the post's issueKey never appears as a chip option).
	 * @param posts
	 */
	public extractAllLabels(posts: BlogPost[]): string[] {
		const labelSet = new Set<string>();
		posts.forEach(post => {
			this.labelsForDisplay(post).forEach(label => labelSet.add(label));
		});
		return Array.from(labelSet).sort();
	}

	/**
	 * Filter posts by search query, label, optional createdAt range, and (for editors) publication state.
	 * @param posts
	 */
	public filterPosts(posts: BlogPost[]): BlogPost[] {
		return posts.filter(post => this.matchesActiveFilters(post, true));
	}

	/**
	 * Posts matching every active filter except the label itself — used to compute which label chips
	 * remain selectable (a label with zero matches under the current search/date/publication filters drops out).
	 * @param posts
	 */
	public postsForLabelOptions(posts: BlogPost[]): BlogPost[] {
		return posts.filter(post => this.matchesActiveFilters(post, false));
	}

	/**
	 * Core predicate shared by {@link filterPosts} and {@link postsForLabelOptions}.
	 * @param post
	 * @param includeLabel Whether to apply the active label filter (false when computing label options themselves).
	 */
	private matchesActiveFilters(post: BlogPost, includeLabel: boolean): boolean {
		const query = this.searchQuery().toLowerCase().trim();
		const fromMs = this.parseDateTimeFilterToMs(this.filterDateFrom(), 'from');
		const toMs = this.parseDateTimeFilterToMs(this.filterDateTo(), 'to');
		const dateRangeInvalid = fromMs != null && toMs != null && fromMs > toMs;
		const hasDateFilter = !dateRangeInvalid && (fromMs != null || toMs != null);
		const activeFilters = this.publicationFilters();

		const matchesSearch =
			!query || post.post.toLowerCase().includes(query) || (post.issueKey || '').toLowerCase().includes(query) || (post.issueLabels || []).some(l => l.toLowerCase().includes(query));
		if (!matchesSearch) {
			return false;
		}

		if (includeLabel) {
			const labelLower = this.activeLabel()?.toLowerCase();
			const matchesLabel = !labelLower || (post.issueLabels || []).some(l => l.toLowerCase() === labelLower);
			if (!matchesLabel) {
				return false;
			}
		}

		if (hasDateFilter) {
			const postMs = this.postCreatedAtMs(post);
			if (postMs == null) {
				return false;
			}
			if (fromMs != null && postMs < fromMs) {
				return false;
			}
			if (toMs != null && postMs > toMs) {
				return false;
			}
		}

		if (activeFilters.size > 0) {
			const matchesPublished = activeFilters.has('published') && post.accepted === true && !post.archived;
			const matchesDraft = activeFilters.has('draft') && post.accepted !== true && !post.archived;
			const matchesArchived = activeFilters.has('archived') && post.archived === true;
			const matchesUntagged = activeFilters.has('untagged') && this.labelsForDisplay(post).length === 0;
			if (!matchesPublished && !matchesDraft && !matchesArchived && !matchesUntagged) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Unix seconds or ms from `createdAt` (backend uses seconds in several flows).
	 * @param post
	 */
	private postCreatedAtMs(post: BlogPost): number | null {
		if (post.createdAt == null || post.createdAt === '') {
			return null;
		}
		const n = Number(post.createdAt);
		if (!Number.isFinite(n)) {
			return null;
		}
		return n < 1e12 ? n * 1000 : n;
	}

	/**
	 * Parses `datetime-local` values (`YYYY-MM-DDTHH:mm` or with seconds). Plain `YYYY-MM-DD` is accepted as
	 * whole local days: From = start of day, To = end of day (inclusive).
	 * @param value
	 * @param bound
	 */
	private parseDateTimeFilterToMs(value: string, bound: 'from' | 'to'): number | null {
		const v = value?.trim();
		if (!v) {
			return null;
		}
		const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
		if (dateOnly) {
			const y = Number(dateOnly[1]);
			const mo = Number(dateOnly[2]) - 1;
			const d = Number(dateOnly[3]);
			const dayStart = new Date(y, mo, d);
			if (dayStart.getFullYear() !== y || dayStart.getMonth() !== mo || dayStart.getDate() !== d) {
				return null;
			}
			if (bound === 'from') {
				return dayStart.getTime();
			}
			return new Date(y, mo, d, 23, 59, 59, 999).getTime();
		}
		const t = new Date(v).getTime();
		return Number.isNaN(t) ? null : t;
	}

	/** True when any filter is active (search, label, parseable date range, or publication for editors). */
	public hasActiveFilters(): boolean {
		return !!this.searchQuery().trim() || this.activeLabel() != null || this.hasDateFilters() || this.publicationFilters().size > 0;
	}

	/**
	 * Toggle a publication/archive chip on/off. Chips are multi-select — multiple can be active at once.
	 * @param mode
	 */
	public togglePublicationFilter(mode: BlogPublicationFilter): void {
		this.publicationFilters.update(current => {
			const next = new Set(current);
			if (next.has(mode)) {
				next.delete(mode);
			} else {
				next.add(mode);
			}
			return next;
		});
	}

	/**
	 * True when `mode` is one of the currently active publication/archive chips.
	 * @param mode
	 */
	public isPublicationFilterActive(mode: BlogPublicationFilter): boolean {
		return this.publicationFilters().has(mode);
	}

	/**
	 * Toggle a label filter on/off. Comparison is case-insensitive so a URL like
	 * `?label=release` toggles off correctly when the stored label is "Release".
	 * @param label
	 */
	public toggleLabel(label: string): void {
		const current = this.activeLabel();
		const isSame = current != null && current.toLowerCase() === label.toLowerCase();
		this.activeLabel.set(isSame ? null : label);
	}

	/**
	 * True when `label` is the currently active filter (case-insensitive).
	 * @param label
	 */
	public isLabelActive(label: string): boolean {
		const current = this.activeLabel();
		return current != null && current.toLowerCase() === label.toLowerCase();
	}

	/** True when From or To has a value that parses (avoids empty/incomplete datetime-local showing as “active”). */
	public hasDateFilters(): boolean {
		const f = this.filterDateFrom().trim();
		const t = this.filterDateTo().trim();
		const fromOk = f.length > 0 && this.parseDateTimeFilterToMs(f, 'from') != null;
		const toOk = t.length > 0 && this.parseDateTimeFilterToMs(t, 'to') != null;
		return fromOk || toOk;
	}

	/**
	 * True when both bounds parse and "From" is after "To" (invalid range).
	 */
	public isDateRangeInvalid(): boolean {
		const f = this.filterDateFrom().trim();
		const t = this.filterDateTo().trim();
		const fromMs = f.length > 0 ? this.parseDateTimeFilterToMs(f, 'from') : null;
		const toMs = t.length > 0 ? this.parseDateTimeFilterToMs(t, 'to') : null;
		if (fromMs == null || toMs == null) {
			return false;
		}
		return fromMs > toMs;
	}

	/** Clears only the date range (From / To). */
	public clearDateFilters(): void {
		this.filterDateFrom.set('');
		this.filterDateTo.set('');
	}

	/**
	 * Clear all filters.
	 */
	public clearFilters(): void {
		this.searchQuery.set('');
		this.activeLabel.set(null);
		this.clearDateFilters();
		this.publicationFilters.set(new Set());
	}

	/**
	 * Estimate reading time in minutes.
	 * @param text
	 */
	public estimateReadTime(text: string): number {
		if (!text) return 1;
		const words = text.trim().split(/\s+/).length;
		return Math.max(1, Math.round(words / 200));
	}

	/**
	 * Truncates text to a specified length.
	 * @param text
	 * @param length
	 */
	public truncateText(text: string, length: number = 300): string {
		if (!text) return '';
		return text.length > length ? text.substring(0, length) + '...' : text;
	}

	/**
	 * Extracts title from post content (assumes first line or specific pattern).
	 * @param post
	 */
	public extractTitle(post: string): string {
		if (!post) return 'Blog Post';

		const lines = post.split('\n');
		const firstLine = lines[0]?.trim();

		if (firstLine && firstLine.length < 100 && !firstLine.endsWith('.')) {
			return firstLine;
		}

		return this.truncateText(post, 50);
	}

	/**
	 * Extracts content from post (everything after title).
	 * @param post
	 */
	public extractContent(post: string): string {
		if (!post) return '';

		const lines = post.split('\n');
		if (lines.length > 1) {
			return lines.slice(1).join('\n').trim();
		}

		return post;
	}

	/**
	 * Opens the read more dialog for a specific post.
	 * @param post
	 */
	public openReadMoreDialog(post: BlogPost): void {
		this.readMorePost = post;
		this.isReadMoreDialogVisible = true;
	}

	/**
	 * Card click opens the reader; clicks on links in the excerpt stay on the link (new tab).
	 * @param event
	 * @param post
	 */
	public onPostCardClick(event: MouseEvent, post: BlogPost): void {
		const el = event.target as HTMLElement | null;
		if (el?.closest('a')) {
			return;
		}
		this.openReadMoreDialog(post);
	}

	/**
	 * Closes the read more dialog.
	 */
	public closeReadMoreDialog(): void {
		this.isReadMoreDialogVisible = false;
		this.readMorePost = null;
	}

	/**
	 * Converts basic markdown to HTML for display.
	 * @param text
	 */
	public markdownToHtml(text: string): string {
		if (!text) return '';

		let html = text
			.replace(/^### (.*$)/gim, '<h3>$1</h3>')
			.replace(/^## (.*$)/gim, '<h2>$1</h2>')
			.replace(/^# (.*$)/gim, '<h1>$1</h1>')
			.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
			.replace(/\*(.*?)\*/gim, '<em>$1</em>')
			.replace(/^- (.*$)/gim, '<li>$1</li>')
			.replace(/\n/gim, '<br>');

		// Wrap consecutive <li> in <ul>
		html = html.replace(/((?:<li>.*<\/li>(?:<br>)*)+)/gim, '<ul>$1</ul>');

		// Remove <br> inside <ul> (between list items)
		html = html.replace(/<ul>[\s\S]*?<\/ul>/gi, match => match.replace(/<br>/g, ''));

		// Remove <br> before and after block-level elements
		html = html.replace(/<br>(<\/?(h[1-6]|ul|ol|blockquote)[^>]*>)/gi, '$1');
		html = html.replace(/(<\/?(h[1-6]|ul|ol|blockquote)[^>]*>)<br>/gi, '$1');

		// Collapse multiple consecutive <br> into one
		html = html.replace(/(<br>\s*){2,}/gi, '<br>');

		return this.linkifyHttpUrls(html);
	}

	/**
	 * Same as {@link markdownToHtml} but trusted for `innerHTML` so `target="_blank"` is preserved.
	 * @param text
	 */
	public markdownBodyHtml(text: string): SafeHtml {
		return this.domSanitizer.bypassSecurityTrustHtml(this.markdownToHtml(text));
	}

	private escapeHtml(text: string): string {
		return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	private escapeHtmlAttr(text: string): string {
		return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;');
	}

	/**
	 * Turns plain URLs (http/https) into anchors; skips existing anchor tags.
	 * @param html
	 */
	private linkifyHttpUrls(html: string): string {
		const saved: string[] = [];
		const marked = html.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, m => {
			saved.push(m);
			return `__BN_ANCHOR_${saved.length - 1}__`;
		});
		const linked = marked.replace(/\b(https?:\/\/[^\s<"'{}|\\^`[\]]+)/gi, raw => {
			let url = raw;
			let trailing = '';
			while (url.length > 0 && '.,;:!?'.includes(url[url.length - 1])) {
				trailing = url[url.length - 1] + trailing;
				url = url.slice(0, -1);
			}
			if (url.length < 8 || !/^https?:\/\//i.test(url)) {
				return raw;
			}
			return `<a href="${this.escapeHtmlAttr(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(url)}</a>${trailing}`;
		});
		return linked.replace(/__BN_ANCHOR_(\d+)__/g, (_, i) => saved[Number(i)] ?? '');
	}

	/**
	 * Formats Unix timestamp to readable date string.
	 * @param timestamp
	 */
	public formatTimestamp(timestamp?: string): string {
		if (!timestamp) return '';

		try {
			const date = new Date(parseInt(timestamp) * 1000);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		} catch {
			return '';
		}
	}

	/**
	 * Formats timestamp as relative time (e.g. "2 days ago").
	 * @param timestamp
	 */
	public formatRelativeTime(timestamp?: string): string {
		if (!timestamp) return '';
		try {
			const date = new Date(parseInt(timestamp) * 1000);
			const now = new Date();
			const diffMs = now.getTime() - date.getTime();
			const diffMins = Math.floor(diffMs / 60000);
			const diffHours = Math.floor(diffMins / 60);
			const diffDays = Math.floor(diffHours / 24);

			if (diffMins < 1) return 'Just now';
			if (diffMins < 60) return `${diffMins}m ago`;
			if (diffHours < 24) return `${diffHours}h ago`;
			if (diffDays < 7) return `${diffDays}d ago`;
			if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
			return this.formatTimestamp(timestamp);
		} catch {
			return '';
		}
	}

	/**
	 * Begin editing a specific post.
	 * @param index
	 * @param post
	 */
	public startEdit(index: number, post: BlogPost): void {
		this.editingIndex = index;
		this.editedTitle = this.extractTitle(post.post);
		this.editedTextValue = this.extractContent(post.post);
		this.editedAccepted = !!post.accepted;
		this.editedArchived = !!post.archived;
		this.editedIssueLabels = Array.isArray(post.issueLabels) ? post.issueLabels.join(',') : (post.issueLabels as unknown as string) || '';
		this.editedIssueKey = post.issueKey ?? '';
		this.openEditorDialog(post);
	}

	/**
	 * Opens the same editor as the card "Edit" action, from the Read more dialog.
	 */
	public editPostFromReadMore(): void {
		if (!this.readMorePost) {
			return;
		}
		const post = this.readMorePost;
		this.editingIndex = null;
		this.editedTitle = this.extractTitle(post.post);
		this.editedTextValue = this.extractContent(post.post);
		this.editedAccepted = !!post.accepted;
		this.editedArchived = !!post.archived;
		this.editedIssueLabels = Array.isArray(post.issueLabels) ? post.issueLabels.join(',') : (post.issueLabels as unknown as string) || '';
		this.editedIssueKey = post.issueKey ?? '';
		this.closeReadMoreDialog();
		this.openEditorDialog(post);
	}

	/**
	 * Open the modal editor populated with the current edited values.
	 * @param post
	 */
	public openEditorDialog(post: BlogPost): void {
		this.dialogContextPost = post;
		this.isEditorDialogVisible = true;
	}

	/**
	 * Close the modal editor without changing inline edit state.
	 */
	public closeEditorDialog(): void {
		this.isEditorDialogVisible = false;
		this.dialogContextPost = null;
		this.editingIndex = null;
		this.editedTitle = '';
	}

	/**
	 * Cancel current edit.
	 */
	public cancelEdit(): void {
		this.editingIndex = null;
		this.editedTitle = '';
		this.editedTextValue = '';
		this.editedAccepted = false;
		this.editedArchived = false;
		this.editedIssueLabels = '';
		this.editedIssueKey = '';
	}

	/**
	 * Save current edits to backend via GraphQL.
	 * @param post
	 */
	public saveEdit(post: BlogPost): void {
		this.launchDarklyService.launchDarklyFlags$.pipe(take(1)).subscribe(flags => {
			if (!flags[BLOGS_NEWS_EDIT_FLAG]) {
				this.error.set('You do not have permission to edit posts');
				return;
			}
			if (!post.postId) {
				this.error.set('Cannot save: missing post identifier');
				return;
			}
			const title = (this.editedTitle || this.extractTitle(post.post)).trim();
			const updatedPost = `${title}\n${this.editedTextValue}`.trim();
			this.isLoading.set(true);
			this.blogNewsService.updateBlogPost$(post, updatedPost, this.editedAccepted, this.editedIssueLabels, this.editedArchived, this.editedIssueKey.trim()).subscribe({
				next: () => {
					this.isLoading.set(false);
					this.editingIndex = null;
					this.editedTitle = '';
					this.editedTextValue = '';
					this.editedAccepted = false;
					this.editedArchived = false;
					this.editedIssueLabels = '';
					this.editedIssueKey = '';
					this.isEditorDialogVisible = false;
					this.dialogContextPost = null;
				},
				error: (e: unknown) => {
					this.isLoading.set(false);
					this.error.set('Failed to update post');
				},
			});
		});
	}

	/**
	 * Toggle archive status of a post directly (without opening editor).
	 * @param post
	 */
	public toggleArchive(post: BlogPost): void {
		this.launchDarklyService.launchDarklyFlags$.pipe(take(1)).subscribe(flags => {
			if (!flags[BLOGS_NEWS_EDIT_FLAG]) {
				this.error.set('You do not have permission to archive posts');
				return;
			}
			if (!post.postId) {
				this.error.set('Cannot archive: missing post identifier');
				return;
			}
			const newArchived = !post.archived;
			this.isLoading.set(true);
			const issueLabels = Array.isArray(post.issueLabels) ? post.issueLabels.join(',') : (post.issueLabels as unknown as string) || '';
			this.blogNewsService.updateBlogPost$(post, post.post, !!post.accepted, issueLabels, newArchived, post.issueKey ?? '').subscribe({
				next: () => {
					this.isLoading.set(false);
				},
				error: (e: unknown) => {
					this.isLoading.set(false);
					this.error.set('Failed to update archive status');
				},
			});
		});
	}
}
